import React from 'react';
import bharat_logo from "./img/bharat_logo.png";
import litvik_logo from "./img/litvik_logo.jpg";
import { useState, useEffect } from "react";
import './Login.css';
import { useCookies } from 'react-cookie';
import { AiOutlineEye, AiOutlineEyeInvisible, AiOutlineHome, AiOutlineMail, AiOutlinePhone } from "react-icons/ai";
import { FaWhatsapp } from "react-icons/fa";
import LoginPlantMap, { PLANT_LOCATIONS } from './components/LoginPlantMap';
import { useNavigate } from 'react-router-dom';
import { getBgImageLogo, submitLogin, verifyAuthToken } from './services/LoginServices';
import { getApiBaseUrl } from "./services/serviceconfig";
import { displayError, getLoginErrorMessage } from './helpers';
import Swal from "sweetalert2";
import {
	AUTH_COOKIE_KEYS,
	establishAuthSession,
	getResolvedAuthToken,
	invalidateAuthSession,
	isAuthSessionValid,
	persistAuthProfile,
} from './utils/authSession';

const FEATURES = [
	{ title: 'Marketing Management', desc: 'Customer, project, and sales order workflows' },
	{ title: 'Planning Management', desc: 'Schedule, production, and dispatch coordination' },
	{ title: 'Dispatch Management', desc: 'Delivery challan, vehicle allocation, and site delivery tracking' },
	{ title: 'Accounting Management', desc: 'Invoice, receipts, payments, banking, adjustments' },
	{ title: 'Fleet Management', desc: 'Trucks, pumps, drivers, diesel, and fleet reports' },
	{ title: 'Quality Control', desc: 'Material analysis, design mix, and QC reports' },
	{ title: 'Inventory Management', desc: 'Vendor, purchase order, GRN, delivery, and stock' },
	{ title: 'Maintenance Management', desc: 'Equipment service, breakdowns, and preventive maintenance' },
];

function Login() {
	const navigate = useNavigate();
	const imageUrl = getApiBaseUrl().slice(0, -4);
	const [inputs, setInputs] = useState({});
	const [tfa, setTfa] = useState({
		'secret_code': '',
		'qrcode': '',
		'otp': ''
	});
	const [error, setError] = useState({});
	const [isLoggingIn, setIsLoggingIn] = useState(false);
	const [loginMessage, setLoginMessage] = useState('');
	const [backendOffline, setBackendOffline] = useState(false);
	const [showTfa, setShowTfa] = useState(false);
	const [showQrCode, setShowQrCode] = useState(true);
	const [cookies, setCookie, removeCookie] = useCookies(AUTH_COOKIE_KEYS);
	const [position, setPosition] = useState({ latitude: 0, longitude: 0 });
	const [companyLogo, setCompanyLogo] = useState("");
	const [companyName, setCompanyName] = useState("");
	const [activePlant, setActivePlant] = useState(PLANT_LOCATIONS[0]?.name || '');
	const [showPassword, setShowPassword] = useState(false);

	const getCompanyLogoSrc = () => {
		if (!companyLogo) {
			return bharat_logo;
		}
		return companyLogo.startsWith('http') ? companyLogo : `${imageUrl}${companyLogo}`;
	};

	useEffect(() => {
		getBgImageLogo()
			.then((response) => {
				if (response?.apiOffline) {
					setBackendOffline(true);
					return;
				}
				const companies = (response?.company_list || []).filter((obj) => obj.status);
				const company = companies.find((item) => item.logo && !item.logo.includes('bharat-logo.jpg'))
					|| companies.find((item) => item.logo)
					|| companies[0];
				if (company?.logo) {
					setCompanyLogo(company.logo);
				}
				if (company?.name) {
					setCompanyName(company.name);
				}
			})
			.catch((error) => {
				console.warn('Login page logo load failed:', error?.message || error);
				if (!error?.response) {
					setBackendOffline(true);
				}
			});
	}, []);

	var options = {
		enableHighAccuracy: true,
		timeout: 5000,
		maximumAge: 0,
	};

	function success(pos) {
		var crd = pos.coords;
		setPosition({
			latitude: crd.latitude,
			longitude: crd.longitude,
		});
	}

	function errors(err) {
		console.warn(`ERROR(${err.code}): ${err.message}`);
	}

	const handleChange = (event) => {
		const name = event.target.name;
		const value = event.target.value;

		if (name === 'username' || name === 'password') {
			setInputs((values) => ({ ...values, [name]: value }));
			if (error[name]) {
				setError((values) => ({ ...values, [name]: '' }));
			}
			if (loginMessage) {
				setLoginMessage('');
			}
			return;
		}

		if (name === 'otp') {
			setTfa((values) => ({ ...values, [name]: value }));
			if (error.otp) {
				setError((values) => ({ ...values, otp: '' }));
			}
			if (loginMessage) {
				setLoginMessage('');
			}
		}
	};

	const validateLoginForm = () => {
		const nextErrors = {};

		if (showTfa) {
			if (!(tfa.otp || '').trim()) {
				nextErrors.otp = 'OTP must not be empty';
			}
			return nextErrors;
		}

		if (!(inputs.username || '').trim()) {
			nextErrors.username = 'User Name must not be empty';
		}
		if (!(inputs.password || '').trim()) {
			nextErrors.password = 'Password must not be empty';
		}

		return nextErrors;
	};

	useEffect(() => {
		if (navigator.geolocation) {
			navigator.permissions
				.query({ name: "geolocation" })
				.then(function (result) {
					if (result.state === "granted") {
						navigator.geolocation.getCurrentPosition(success);
					}
					else if (result.state === "prompt") {
						navigator.geolocation.getCurrentPosition(success, errors, options);
					}
					else if (result.state === "denied") {
						console.log("If denied then you have to show instructions to enable location")
					}
				})
				.catch((error) => {
					console.warn('Geolocation permission check failed:', error?.message || error);
				});
		}
		else {
			Swal.fire("Sorry Not available!", "", "info");
		}
	}, []);

	useEffect(() => {
		let cancelled = false;

		const restoreSession = async () => {
			try {
				const authToken = getResolvedAuthToken(cookies);
				if (!authToken) {
					return;
				}
				if (!isAuthSessionValid(authToken)) {
					invalidateAuthSession(removeCookie);
					return;
				}

				const isValidToken = await verifyAuthToken({ myToken: authToken });
				if (cancelled) {
					return;
				}
				if (isValidToken) {
					navigate('/Home', { replace: true });
					return;
				}
				invalidateAuthSession(removeCookie);
			} catch (error) {
				console.warn('Session restore failed:', error?.message || error);
				if (!cancelled) {
					setBackendOffline(true);
				}
			}
		};

		restoreSession();

		return () => {
			cancelled = true;
		};
	}, [cookies.myToken, navigate, removeCookie]);

	const handleSubmit = (event) => {
		event.preventDefault();
		const validationErrors = validateLoginForm();
		setError(validationErrors);
		if (Object.keys(validationErrors).length > 0) {
			setLoginMessage(Object.values(validationErrors).join(' '));
			return;
		}

		setLoginMessage('');
		setIsLoggingIn(true);
		submitLogin(inputs.username, inputs.password, tfa.otp, position.latitude, position.longitude)
			.then(function (login_response) {
				if (login_response.tfa_enabled === false) {
					setLoginMessage('');
					setTfa((values) => ({
						...values,
						secret_code: login_response.otp_base32,
						qrcode: login_response.img_str,
					}));
					setInputs((values) => ({ ...values, username: login_response.username }));
					setShowQrCode(true);
					setShowTfa(true);
				} else if (login_response.tfa_enabled === true && login_response.state === 'have to verify otp') {
					setLoginMessage('');
					setShowQrCode(false);
					setShowTfa(true);
				} else if (login_response.token) {
					setLoginMessage('');
					const cookieOptions = { path: '/' };
					establishAuthSession(login_response.token);
					setCookie('myToken', login_response.token, cookieOptions);
					setCookie('myName', login_response.name, cookieOptions);
					if (login_response.user_id) {
						setCookie('myUserId', login_response.user_id, cookieOptions);
					}
					persistAuthProfile(login_response, setCookie, removeCookie);

					navigate('/Home', { replace: true });
				} else {
					const failureMessage = typeof login_response?.detail === 'string'
						? login_response.detail
						: 'Login could not be completed. Contact your administrator.';
					setLoginMessage(failureMessage);
					displayError(login_response, 'Login Failed');
				}
			})
			.catch((error) => {
				error.__authHandled = true;
				console.log(error?.response?.data || error);
				const failureMessage = getLoginErrorMessage(error, getApiBaseUrl());
				setLoginMessage(failureMessage);
				if (!error?.response) {
					setBackendOffline(true);
				}
				displayError(
					error?.response?.data || failureMessage,
					"Login Failed",
				);
			})
			.finally(() => {
				setIsLoggingIn(false);
			});
	};

	return (
		<div className="login-page">
			{!showTfa ? (
				<>
					<div className="login-page__grid">
						<aside className="login-card login-card--features">
							<span className="login-card__eyebrow">Modules</span>
							<h2 className="login-card__title">ERP Features</h2>
							<p className="login-card__lead">
								Litvik ERP simplifies ready-mix concrete operations with integrated daily workflows.
							</p>
							<ul className="login-feature-list">
								{FEATURES.map((feature) => (
									<li key={feature.title} className="login-feature-list__item">
										<span className="login-feature-list__bullet" />
										<div>
											<span className="login-feature-list__title">{feature.title}</span>
											<span className="login-feature-list__desc">{feature.desc}</span>
										</div>
									</li>
								))}
							</ul>
						</aside>

						<main className="login-card login-card--form">
							<div className="login-card__avatar-wrap">
								<img
									src={getCompanyLogoSrc()}
									alt={companyName || 'Company logo'}
									className="login-card__avatar"
									onError={(event) => {
										event.currentTarget.onerror = null;
										event.currentTarget.src = bharat_logo;
									}}
								/>
							</div>
							{companyName ? (
								<h2 className="login-card__company-name">{companyName}</h2>
							) : null}
							<form className="login-form" onSubmit={handleSubmit}>
								{backendOffline ? (
									<div className="login-alert login-alert--warning" role="alert">
										<strong>Server unavailable.</strong> Cannot reach {getApiBaseUrl()}. Start the Django backend on port 8000, or update <code>REACT_APP_API_BASE_URL</code> in <code>frontend/.env.development</code>.
									</div>
								) : null}
								{loginMessage ? (
									<div className="login-alert login-alert--danger" role="alert">
										<strong>Login failed:</strong> {loginMessage}
									</div>
								) : null}
								<div className="login-form__group">
									<label className="login-form__label" htmlFor="username">User Name</label>
									<div className="login-form__field">
										<span className="login-form__field-icon"><i className="fa fa-user" /></span>
										<input
											id="username"
											className="login-form__input"
											type="text"
											name="username"
											required
											onChange={handleChange}
											value={inputs.username || ""}
											placeholder="Enter username"
										/>
									</div>
									{error.username ? <p className="login-form__error">{error.username}</p> : null}
								</div>

								<div className="login-form__group">
									<label className="login-form__label" htmlFor="password">Password</label>
									<div className="login-form__field">
										<span className="login-form__field-icon"><i className="fa fa-lock" /></span>
										<input
											id="password"
											className="login-form__input login-form__input--password"
											type={showPassword ? "text" : "password"}
											name="password"
											required
											onChange={handleChange}
											value={inputs.password || ""}
											placeholder="Enter password"
											autoComplete="current-password"
										/>
										<button
											type="button"
											className="login-form__toggle-password"
											onClick={() => setShowPassword((visible) => !visible)}
											aria-label={showPassword ? "Hide password" : "Show password"}
											aria-pressed={showPassword}
										>
											{showPassword ? <AiOutlineEyeInvisible size={18} /> : <AiOutlineEye size={18} />}
										</button>
									</div>
									{error.password ? <p className="login-form__error">{error.password}</p> : null}
								</div>

								<input
									className="login-form__submit"
									type="submit"
									value={isLoggingIn ? 'Logging in...' : 'Log In'}
									disabled={isLoggingIn}
								/>
							</form>
						</main>

						<aside className="login-card login-card--locations">
							<span className="login-card__eyebrow">Plants</span>
							<h2 className="login-card__title">Our Locations</h2>
							<p className="login-card__lead">
								Bharat Concrete ready-mix plants across Tamil Nadu.
							</p>
							<div className="login-locations-layout">
								<ul className="login-location-list">
									{PLANT_LOCATIONS.map((location) => (
										<li key={location.name}>
											<button
												type="button"
												className={`login-location-list__item${activePlant === location.name ? ' is-active' : ''}`}
												onClick={() => setActivePlant(location.name)}
											>
												<span className="login-location-list__bullet" />
												<span className="login-location-list__copy">
													<span className="login-location-list__title">{location.name}</span>
													<span className="login-location-list__area">{location.area}</span>
												</span>
											</button>
										</li>
									))}
								</ul>
								<LoginPlantMap
									locations={PLANT_LOCATIONS}
									activeName={activePlant}
									onSelect={setActivePlant}
								/>
							</div>
						</aside>
					</div>
				</>
			) : (
				<div className="login-page__tfa">
					<div className="login-card login-tfa-card">
						<h2 className="login-tfa-card__title">Two-Factor Authentication</h2>
						{showQrCode ? (
							<>
								<p className="login-tfa-card__hint">Scan the QR code with your authenticator app</p>
								<img
									src={`data:image/jpeg;base64,${tfa.qrcode}`}
									alt="2FA QR code"
									className="login-tfa-card__qr"
								/>
							</>
						) : (
							<p className="login-tfa-card__hint">Enter the one-time password from your authenticator app</p>
						)}
						<form className="login-form" onSubmit={handleSubmit}>
							{loginMessage ? (
								<div className="login-alert login-alert--danger" role="alert">
									<strong>Verification failed:</strong> {loginMessage}
								</div>
							) : null}
							<div className="login-form__group">
								<label className="login-form__label" htmlFor="otp">One-Time Password</label>
								<input
									id="otp"
									className="login-form__input"
									style={{ paddingLeft: '14px' }}
									type="text"
									name="otp"
									placeholder="Enter OTP"
									onChange={handleChange}
									value={tfa.otp || ""}
									required
								/>
								{error.otp ? <p className="login-form__error">{error.otp}</p> : null}
							</div>
							<button type="submit" className="login-tfa-card__submit" disabled={isLoggingIn}>
								{isLoggingIn ? 'Verifying...' : 'Verify & Activate'}
							</button>
						</form>
					</div>
				</div>
			)}

			<footer className="login-page__footer">
				<div className="login-page__footer-brand">
					<img src={litvik_logo} alt="Litvik" className="login-page__brand-logo" />
					<div className="login-page__brand-text">
						<h1 className="login-page__brand-title">Litvik Software Labs Pvt. Ltd.</h1>
						<p className="login-page__brand-kicker">Ready Mix Concrete ERP</p>
					</div>
				</div>

				<div className="login-page__footer-contacts">
					<a
						className="login-page__footer-link"
						href="https://wa.me/919551614121"
						target="_blank"
						rel="noreferrer"
					>
						<span className="login-page__footer-icon login-page__footer-icon--whatsapp" aria-hidden="true">
							<FaWhatsapp size={15} />
						</span>
						<span className="login-page__footer-icon" aria-hidden="true">
							<AiOutlinePhone size={15} />
						</span>
						+91 9551614121
					</a>
					<a className="login-page__footer-link" href="mailto:support@litvik.in">
						<span className="login-page__footer-icon" aria-hidden="true">
							<AiOutlineMail size={15} />
						</span>
						support@litvik.in
					</a>
				</div>

				<div className="login-page__footer-meta">
					<span>litvik@2026-27</span>
					<span>developed &amp; maintenance by litvik</span>
				</div>

				<div className="login-page__footer-address">
					<span className="login-page__footer-icon" aria-hidden="true">
						<AiOutlineHome size={15} />
					</span>
					Plot No.1, Second Floor, Thiruvalluvar Cross Street, Madambakkam, Chennai - 600126
				</div>
			</footer>
		</div>
	);
}
export default Login;
